import React, { useEffect, useRef, useState } from 'react'
import Slider, { Settings } from 'react-slick'

import { SanityTabsCarousel, SanityTabsCarouselTab } from '../../../graphql/gatsby'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { useSanityRoleBasedContent } from '../../../lib/sanity'
import { cn } from '../../utils/cn'
import { TabsCarouselActiveTab, TabsCarouselActiveTabProps } from './ActiveTab'
import { TabsCarouselTab } from './Tab'

import PrevIcon from '../../../img/icons/arrow-left.svg'
import NextIcon from '../../../img/icons/arrow-right.svg'

const sliderConfig: Settings = {
  dots: false,
  infinite: false,
  speed: 500,
  swipe: true,
  touchMove: true,
  swipeToSlide: true,
  arrows: false,
  variableWidth: true,
  slidesToShow: 3,
  slidesToScroll: 3,
  centerMode: false,
  initialSlide: 0,
  responsive: [
    {
      breakpoint: 768,
      settings: {
        // variableWidth: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        // centerMode: false,
        // centerPadding: '0px',
      },
    },
    {
      breakpoint: 1440,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },
  ],
}

export const TabsCarousel: React.FC<SanityTabsCarousel> = ({
  title,
  label,
  tabs,
  roleBasedContent,
}) => {
  const sliderRef = useRef<Slider>(null)
  const { shouldDisplay } = useSanityRoleBasedContent({ roleBasedContent })
  const [currentSlide, setCurrentSlide] = useState(0)
  const [activeTab, setActiveTab] = useState<SanityTabsCarouselTab | null>()

  // Track CTA clicks
  const setAnalyticsEventOnClick: TabsCarouselActiveTabProps['onCtaClick'] = (
    cta,
    link,
    tabTitle,
  ) => {
    setAnalyticsEvent({
      event: 'select_content',
      item_id: `Tab:${tabTitle}-Button-${cta.title || ''}`,
      content_type: 'carousel',
      module_name: 'TabsCarousel',
      label_text: cta.title || '',
      link_destination: link._tag === 'Normalized' ? link.href ?? undefined : link._tag,
      subtitle: tabTitle || '',
      title: title || '',
    })
  }

  const selectTab = (tab: SanityTabsCarouselTab) => {
    setActiveTab(tab)
    const index = tabs?.findIndex((t) => t?._key === tab._key)
    if (sliderRef.current && index != null && index >= 0) {
      sliderRef.current.slickGoTo(index)
    }
  }

  const handleAfterChange = (index: number) => {
    setCurrentSlide(index)
  }

  useEffect(() => {
    if (!tabs || tabs.length === 0) {
      return
    }
    setActiveTab(tabs[0])
  }, [tabs])

  if (!shouldDisplay || !tabs) {
    return null
  }

  return (
    <div className="relative flex flex-col w-full bg-white">
      <div className="m-auto w-full h-2 bg-gradient-bar-2" />
      <div className="flex flex-col py-10 md:py-[60px] px-6 md:px-[64px] xl:px-[80px]">
        <div>
          {label && (
            <p className="font-semibold uppercase text-sm text-brand-navy mb-4 md:mb-6">{label}</p>
          )}
          <div className="flex flex-col md:flex-row items-start justify-between content-center">
            <h1 className="font-medium text-[32px] mb-5 md:mb-0 md:text-[40px] text-brand-navy">
              {title}
            </h1>

            <div className="flex space-x-4">
              <button
                onClick={() => sliderRef.current?.slickPrev()}
                className="w-[65px] h-[65px] rounded-full border border-brand-navy flex items-center justify-center bg-white shadow disabled:opacity-50"
                aria-label="Previous"
                disabled={currentSlide === 0}
              >
                <img src={PrevIcon} alt="Previous" />
              </button>
              <button
                onClick={() => sliderRef.current?.slickNext()}
                className="w-[65px] h-[65px] rounded-full border border-brand-navy flex items-center justify-center bg-white shadow disabled:opacity-50"
                aria-label="Next"
                disabled={currentSlide >= tabs?.length - 1}
              >
                <img src={NextIcon} alt="Next" />
              </button>
            </div>
          </div>
          <Slider
            ref={sliderRef}
            afterChange={handleAfterChange}
            {...sliderConfig}
            className={cn(
              'relative w-full bg-white mt-5 md:mt-8',

              '[&>.slick-list]:overflow-visible',
              '[&>.slick-list>.slick-track]:!flex',
              '[&>.slick-list>.slick-track]:gap-6',

              'md:[&>.slick-list>.slick-track>.slick-slide]:flex-shrink-0',
              'md:[&>.slick-list>.slick-track>.slick-slide]:!w-auto',
              'md:[&>.slick-list>.slick-track>.slick-slide]:px-3',

              '[&>.slick-list>.slick-track]:!mx-0',
              '[&>.slick-list>.slick-track]:justify-start',
            )}
          >
            {tabs.map((tab) => {
              return (
                tab && (
                  <TabsCarouselTab
                    key={tab._key}
                    tab={tab}
                    isActive={activeTab?._key === tab._key}
                    selectTab={selectTab}
                  />
                )
              )
            })}
          </Slider>
        </div>
        {activeTab && (
          <TabsCarouselActiveTab activeTab={activeTab} onCtaClick={setAnalyticsEventOnClick} />
        )}
      </div>
      <div className={cn('m-auto w-[90%] h-1', 'bg-brand-orange')} />
    </div>
  )
}
